/**
 * GrowthPage
 *
 * /growth route — Phase 2 surface. Three stacked regions:
 *   1. Suggested (LLM) — bootstrap banner + suggested cards w/ Accept/Dismiss
 *   2. Active (max 5)  — "X of 5 active focus areas" + Add button + cards
 *   3. Archive         — retired cards, most recent first
 *
 * Accept flow: calls activate; on 409 cap_reached opens RetireConfirmModal
 * targeting the first active focus area. After retire succeeds user can
 * re-accept the suggestion.
 * Empty state: no active, retired, or suggested focus areas → explanatory block.
 */

import { useState } from "react";
import { Plus, Sparkles, ChevronDown } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { FocusAreaCard } from "@/components/growth/FocusAreaCard";
import { FocusAreaFormModal } from "@/components/growth/FocusAreaFormModal";
import { RetireConfirmModal } from "@/components/growth/RetireConfirmModal";
import { SuggestedFocusAreaCard } from "@/components/growth/SuggestedFocusAreaCard";
import { BootstrapJustRanBanner } from "@/components/growth/BootstrapJustRanBanner";
import { useFocusAreas } from "@/hooks/useFocusAreas";
import {
  useActivateFocusArea,
  useCreateFocusArea,
  useDismissFocusArea,
  useRetireFocusArea,
  useUpdateFocusArea,
} from "@/hooks/useFocusAreaMutations";
import type { FocusArea } from "@/types/focusArea";

function isCapError(err: unknown): boolean {
  const e = err as {
    response?: { status?: number; data?: { detail?: { code?: string } } };
    message?: string;
  };
  if (e?.response?.status === 409) return true;
  if (e?.response?.data?.detail?.code === "active_cap_reached") return true;
  if (typeof e?.message === "string" && /cap/i.test(e.message)) return true;
  return false;
}

export function GrowthPage() {
  const {
    focusAreas: activeFocusAreas,
    isLoading: activeLoading,
    activeCount,
    maxActive,
  } = useFocusAreas("active");
  const { focusAreas: retiredFocusAreas } = useFocusAreas("retired");
  const { focusAreas: suggestedFocusAreas, bootstrapJustRan } =
    useFocusAreas("suggested");
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);

  // Capacity comes from the server so the limit lives in exactly one place.
  //
  // activeCount can EXCEED maxActive: the work-in-progress limit was lowered
  // from 5 to 3, and users already over it are not force-retired. Clamping at
  // zero keeps that from producing negative slots and a crash in
  // Array.from({ length }).
  const isOverCapacity = activeCount > maxActive;
  const openSlots = Math.max(0, maxActive - activeCount);

  // One suggestion per open slot; the rest fold away. Showing three at once is
  // what made the page read as a queue of homework.
  const slotSuggestions = suggestedFocusAreas.slice(0, openSlots);
  const overflowSuggestions = suggestedFocusAreas.slice(openSlots);
  const emptySlotCount = Math.max(0, openSlots - slotSuggestions.length);

  const createMutation = useCreateFocusArea();
  const updateMutation = useUpdateFocusArea();
  const retireMutation = useRetireFocusArea();
  const activateMutation = useActivateFocusArea();
  const dismissMutation = useDismissFocusArea();

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FocusArea | null>(null);
  const [retireTarget, setRetireTarget] = useState<FocusArea | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const hasAnything =
    activeFocusAreas.length > 0 ||
    retiredFocusAreas.length > 0 ||
    suggestedFocusAreas.length > 0;

  const handleCreate = async (payload: {
    name: string;
    description: string;
    practice_text: string;
  }) => {
    try {
      await createMutation.mutateAsync(payload);
    } catch (err) {
      if (isCapError(err)) {
        throw new Error("CAP");
      }
      throw err;
    }
  };

  const handleUpdate = async (payload: {
    name: string;
    description: string;
  }) => {
    if (!editTarget) return;
    await updateMutation.mutateAsync({
      focusAreaId: editTarget.id,
      payload,
    });
  };

  const handleRetire = async (reason: string) => {
    if (!retireTarget) return;
    await retireMutation.mutateAsync({
      focusAreaId: retireTarget.id,
      payload: { retired_reason: reason },
    });
  };

  const handleAccept = async (id: string) => {
    try {
      await activateMutation.mutateAsync(id);
    } catch (err) {
      if (isCapError(err)) {
        // Open retire modal targeting the first active focus area
        if (activeFocusAreas.length > 0) {
          setRetireTarget(activeFocusAreas[0]);
        }
      }
    }
  };

  const handleDismiss = async (id: string) => {
    await dismissMutation.mutateAsync(id);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <div>
              <h1 className="text-2xl font-bold leading-tight">Growth</h1>
              {!activeLoading && (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="wip-capacity"
                >
                  {isOverCapacity
                    ? `Working on ${activeCount} — over the limit of ${maxActive}`
                    : `Working on ${activeCount} of ${maxActive}`}
                </p>
              )}
            </div>
          </div>
          {/* Offered only when there is somewhere to put it. A disabled button
              still invites the click; absence is clearer. */}
          {openSlots > 0 && (
            <Button
              onClick={() => setAddOpen(true)}
              className="gap-1"
              disabled={activeLoading}
            >
              <Plus className="w-4 h-4" />
              Add
            </Button>
          )}
        </div>

        {bootstrapJustRan &&
          !bannerDismissed &&
          suggestedFocusAreas.length > 0 && (
            <BootstrapJustRanBanner
              onDismiss={() => setBannerDismissed(true)}
            />
          )}

        {!hasAnything && !activeLoading && (
          <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground">
            <p className="font-medium">No focus areas yet.</p>
            <p className="text-sm mt-1">
              Name one behavior you'd like to work on, and add a small practice.
            </p>
          </div>
        )}

        {/* Slots. What you are working on comes first; suggestions only ever
            appear inside an open slot. */}
        {(activeFocusAreas.length > 0 || openSlots > 0) && (
          <section className="mb-8 space-y-3">
            {activeFocusAreas.map((fa) => (
              <FocusAreaCard
                key={fa.id}
                focusArea={fa}
                onEdit={(f) => setEditTarget(f)}
                onRetire={(f) => setRetireTarget(f)}
              />
            ))}

            {slotSuggestions.map((fa) => (
              <SuggestedFocusAreaCard
                key={fa.id}
                focusArea={fa}
                onAccept={(id) => void handleAccept(id)}
                onDismiss={(id) => void handleDismiss(id)}
                isAccepting={activateMutation.isPending}
                isDismissing={dismissMutation.isPending}
              />
            ))}

            {/* Genuinely empty slots — nothing active, nothing suggested to
                offer. Shown as an invitation rather than left blank. */}
            {Array.from({ length: emptySlotCount }).map((_, index) => (
              <div
                key={`empty-slot-${index}`}
                className="rounded-lg border border-dashed p-5 text-center"
                data-testid="empty-slot"
              >
                <p className="text-sm font-medium text-foreground">Open slot</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  You don't have to fill it. Add something when you're ready.
                </p>
              </div>
            ))}
          </section>
        )}

        {/* At capacity the app stops proposing work entirely. */}
        {openSlots <= 0 && suggestedFocusAreas.length > 0 && (
          <p
            className="mb-8 text-center text-xs text-muted-foreground"
            data-testid="capacity-note"
          >
            {suggestedFocusAreas.length} suggestion
            {suggestedFocusAreas.length === 1 ? "" : "s"} waiting. Retire
            something to make room.
          </p>
        )}

        {/* Anything that did not fit in a slot stays folded away. */}
        {overflowSuggestions.length > 0 && (
          <section className="mb-8">
            <button
              type="button"
              onClick={() => setShowAllSuggestions((open) => !open)}
              aria-expanded={showAllSuggestions}
              className="mb-3 inline-flex items-center gap-1 rounded text-sm font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="more-suggestions-toggle"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showAllSuggestions ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
              More suggestions ({overflowSuggestions.length})
            </button>
            {showAllSuggestions && (
              <div className="space-y-3">
                {overflowSuggestions.map((fa) => (
                  <SuggestedFocusAreaCard
                    key={fa.id}
                    focusArea={fa}
                    onAccept={(id) => void handleAccept(id)}
                    onDismiss={(id) => void handleDismiss(id)}
                    isAccepting={activateMutation.isPending}
                    isDismissing={dismissMutation.isPending}
                    canAccept={openSlots > 0}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {retiredFocusAreas.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Archive</h2>
            <div className="space-y-3">
              {retiredFocusAreas.map((fa) => (
                <FocusAreaCard
                  key={fa.id}
                  focusArea={fa}
                  onEdit={() => {}}
                  onRetire={() => {}}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      <FocusAreaFormModal
        open={addOpen}
        mode="add"
        onOpenChange={setAddOpen}
        onSubmit={handleCreate}
      />
      {editTarget && (
        <FocusAreaFormModal
          open={!!editTarget}
          mode="edit"
          focusArea={editTarget}
          onOpenChange={(o) => !o && setEditTarget(null)}
          onSubmit={handleUpdate}
        />
      )}
      {retireTarget && (
        <RetireConfirmModal
          open={!!retireTarget}
          focusAreaName={retireTarget.name}
          onOpenChange={(o) => !o && setRetireTarget(null)}
          onConfirm={handleRetire}
        />
      )}
    </Layout>
  );
}
